import { GeckoFeatureModelType } from 'types/assetModel/assetModelEnums'
import {
  FeatureTableOptions,
  GeckoEditableFeature,
} from 'types/assetModel/features/baseClasses/geckoEditableFeature'
import {
  ExternalUserFieldAccess,
  FormField,
  getFormFieldByDataKey,
  InputType,
} from 'types/geckoFormTypesAndHelpers'
import { GeckoComponentRect } from 'types/geometric/geckoComponentRect'
import { GeckoMeasurementUnit } from 'types/measurement/geckoMeasurementUnit'
import { requiredNumberSchema, requiredStringSchema } from 'utils/zodHelpers'

export type GeckoSphericalPlateJSON = {
  type: GeckoFeatureModelType.SphericalPlate
  name: string
  feature_geometry: {
    // Spherical coordinates
    phi_start: number // longitude angle (0 to 2pi)
    phi_end: number
    theta_start: number // latitude angle (0 to pi/2 for half-sphere)
    theta_end: number
  }
}

export const NewGeckoSphericalPlateJSON: GeckoSphericalPlateJSON = {
  type: GeckoFeatureModelType.SphericalPlate,
  name: 'New Spherical Plate',
  feature_geometry: {
    phi_start: 0,
    phi_end: 0,
    theta_start: 0,
    theta_end: 0,
  },
}

export class GeckoSphericalPlate extends GeckoEditableFeature {
  readonly name: string
  readonly type = GeckoFeatureModelType.SphericalPlate
  readonly geometry: GeckoComponentRect

  private constructor(id: string, name: string, geometry: GeckoComponentRect) {
    super(id)
    this.name = name
    this.type = GeckoFeatureModelType.SphericalPlate
    this.geometry = geometry
  }

  public static CreateFromJSON(
    componentID: string,
    plateID: string,
    plateJSON: GeckoSphericalPlateJSON
  ): GeckoSphericalPlate {
    const xDimensionMeasurementUnits = GeckoMeasurementUnit.Degrees
    const yDimensionMeasurementUnits = GeckoMeasurementUnit.Degrees

    const name = plateJSON.name
    const featureGeometryData = plateJSON.feature_geometry as Record<
      string,
      number
    >

    // Store the values in degrees for the geometry
    const plateGeometry = GeckoComponentRect.CreateFromXYs(
      componentID as string,
      featureGeometryData.phi_start as number,
      featureGeometryData.theta_start as number,
      featureGeometryData.phi_end as number,
      featureGeometryData.theta_end as number,
      xDimensionMeasurementUnits,
      yDimensionMeasurementUnits
    )

    return new GeckoSphericalPlate(plateID, name, plateGeometry)
  }

  public static CreateMapFromJSON(
    componentID: string,
    recordsJSON: Record<string, GeckoSphericalPlateJSON>
  ): Map<string, GeckoSphericalPlate> {
    return Object.keys(recordsJSON).reduce((resultObj, plateID) => {
      resultObj.set(
        plateID,
        GeckoSphericalPlate.CreateFromJSON(
          componentID,
          plateID,
          recordsJSON[plateID]
        )
      )
      return resultObj
    }, new Map<string, GeckoSphericalPlate>())
  }

  asJSON() {
    return generateSphericalPlate(this.name, this.geometry)
  }
}

export function generateSphericalPlate(
  name: string,
  geometry: GeckoComponentRect
): GeckoSphericalPlateJSON {
  return {
    type: GeckoFeatureModelType.SphericalPlate,
    name: name,
    feature_geometry: {
      phi_start: geometry.x.start.num,
      phi_end: geometry.x.end.num,
      theta_start: geometry.y.start.num,
      theta_end: geometry.y.end.num,
    },
  }
}

export const SphericalPlateFormFields = (
  sphericalPlateJSON: GeckoSphericalPlateJSON
): FormField[] => {
  return [
    {
      dataKey: 'name',
      label: 'Plate Name',
      value: sphericalPlateJSON.name,
      unit_magnitude: null,
      validationRule: requiredStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    {
      dataKey: 'phi_start',
      label: 'Latitude Start (degrees)',
      value: sphericalPlateJSON.feature_geometry.phi_start,
      unit_magnitude: null,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'phi_end',
      label: 'Latitude End (degrees)',
      value: sphericalPlateJSON.feature_geometry.phi_end,
      unit_magnitude: null,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'theta_start',
      label: 'Longitude Start (degrees)',
      value: sphericalPlateJSON.feature_geometry.theta_start,
      unit_magnitude: null,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'theta_end',
      label: 'Longitude End (degrees)',
      value: sphericalPlateJSON.feature_geometry.theta_end,
      unit_magnitude: null,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
  ]
}

export const SphericalPlateJSONFromFormFields = (
  formFields: FormField[]
): GeckoSphericalPlateJSON => {
  return {
    type: GeckoFeatureModelType.SphericalPlate,
    name: getFormFieldByDataKey(formFields, 'name')?.value as string,
    feature_geometry: {
      phi_start: getFormFieldByDataKey(formFields, 'phi_start')
        ?.value as number,
      phi_end: getFormFieldByDataKey(formFields, 'phi_end')?.value as number,
      theta_start: getFormFieldByDataKey(formFields, 'theta_start')
        ?.value as number,
      theta_end: getFormFieldByDataKey(formFields, 'theta_end')
        ?.value as number,
    },
  }
}

export const SphericalPlateFeatureTableOptions: FeatureTableOptions = {
  type: GeckoFeatureModelType.SphericalPlate,
  title: 'Plates',
  singularName: 'Plate',
  fields: ['phi_start', 'phi_end', 'theta_start', 'theta_end'],
  sortBy: [{ fieldName: 'theta_start', order: 'asc' }],
}
